import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import React from "react";
import {
  AutocompleteInput,
  CreateButton,
  Datagrid,
  EditButton,
  List,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  ShowButton,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useStore,
} from "react-admin";
import ProfessorField from "server/components/Fields/ProfessorField";
import { checkPermission } from "server/security/utils";
import CustomFilter from "features/CustomFilter";
import useInternshipFilter from "features/filters/useInternshipFilter";
import ProfessorInput from "server/components/Fields/ProfessorInput";
import SecuredComponent from "server/security/SecuredComponent";
import CloneTrainingBaseDialog from "./CloneTrainingBaseDialog";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import { InternshipSessionField } from "server/components/Fields/InternshipSessionInput";
import { administrationRequests } from "api/administrationApi";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import { MOBILE_BREAKPOINT } from "utils/constants";

const STORE_KEY = "trainingBase.filters";

const ListTrainingBases = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const [filters, setFilters] = useStore(STORE_KEY, null);
  const internship = useInternshipFilter({ filters, sessionRequired: false });

  const handleSearch = selectedFilters => {
    setFilters(selectedFilters);
  };

  const trainingBaseFilters = [
    <ReferenceInput
      source="buildingId"
      reference={administrationRequests.getBuildingsSelect()}
      label="Сграда"
      alwaysOn
    >
      <AutocompleteInput
        label="Сграда"
        optionText="name"
        optionValue="id"
        filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
        style={{ width: 300 }}
      />
    </ReferenceInput>,
    <ReferenceInput
      source="wardId"
      reference={administrationRequests.getWardsSelect()}
      alwaysOn
    >
      <SelectInput
        optionText="name"
        label="Болнично отделение"
        emptyText={false}
        style={{ width: 200 }}
      />
    </ReferenceInput>,
    <ProfessorInput
      source="employeeId"
      alwaysOn
      style={{ width: 200 }}
    />,
  ];

  const ListActions = hasCreate => {
    return (
      <TopToolbar>
        <SecuredComponent permission={permissions.canCloneTrainingBases}>
          <CloneTrainingBaseDialog filters={filters} />
        </SecuredComponent>
        {hasCreate && <CreateButton />}
      </TopToolbar>
    );
  };

  return (
    <>
      {!filters && <Title title={"Учебни бази"} />}
      <CustomFilter
        title="Избери филтри"
        cardWidth={isMobile ? "100%" : "700px"}
        filters={internship.fields}
        buttonLabel="Покажи"
        buttonDisabled={!internship.isComplete}
        buttonOnClick={() => handleSearch(internship.query)}
      />
      {!filters ? (
        <Paper sx={{ mt: 5 }}>
          <Box
            p={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              align="center"
              color="secondary"
            >
              Въведи филтри за Учебни бази или създай нова
            </Typography>
            <SecuredComponent permission={permissions.canManageTrainingBases}>
              <CreateButton />
            </SecuredComponent>
          </Box>
        </Paper>
      ) : (
        <List
          exporter={false}
          title="Учебни бази"
          filters={trainingBaseFilters}
          filter={filters}
          actions={<ListActions hasCreate={checkPermission(getPermissions(), permissions.canManageTrainingBases)} />}
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Стажантска сесия", value: <InternshipSessionField /> }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Сграда",
                        value: (
                          <ReferenceField
                            source="buildingId"
                            reference={administrationRequests.getBuildingsSelect()}
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                </div>
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow
                    fields={[
                      {
                        label: "Болнично отделение",
                        value: (
                          <ReferenceField
                            source="wardId"
                            reference={administrationRequests.getWardsSelect()}
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[{ label: "Преподавател", value: <ProfessorField source="employeeId" /> }]}
                  />
                </div>
              )}
              tertiaryText={() => (
                <>
                  <SecuredComponent permission={permissions.canPreviewTrainingBases}>
                    <ShowButton label="" />
                  </SecuredComponent>
                  <SecuredComponent permission={permissions.canManageTrainingBases}>
                    <EditButton label="" />
                  </SecuredComponent>
                </>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
            >
              <InternshipSessionField label="Стажантска сесия" />
              <ReferenceField
                source="buildingId"
                reference={administrationRequests.getBuildingsSelect()}
                label="Сграда"
                sortable={false}
              >
                <TextField source="name" />
              </ReferenceField>
              <ReferenceField
                source="wardId"
                reference={administrationRequests.getWardsSelect()}
                label="Болнично отделение"
                sortable={false}
              >
                <TextField source="name" />
              </ReferenceField>
              <ProfessorField
                source="employeeId"
                label="Преподавател"
                sortable={false}
              />
              <SecuredComponent permission={permissions.canPreviewTrainingBases}>
                <ShowButton />
              </SecuredComponent>
              <SecuredComponent permission={permissions.canManageTrainingBases}>
                <EditButton />
              </SecuredComponent>
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListTrainingBases;
